// src/constants/roleTemplates.js
// Role templates that serve as blueprints for tenant-specific roles
// These are NOT database records - they're used to preseed new tenants

import { PERMISSIONS } from "./permissions.js";

// ============================================================================
// ROLE TEMPLATE KEYS
// ============================================================================

/**
 * Template keys for role identification
 * Used to track which template a tenant role was created from
 */
export const ROLE_TEMPLATE_KEYS = {
  PRESIDENT: "PRESIDENT",
  VICE_PRESIDENT: "VICE_PRESIDENT",
  SECRETARY: "SECRETARY",
  TREASURER: "TREASURER",
  TRUSTEE: "TRUSTEE",
  EBOARD_REP: "EBOARD_REP",
  GRIEVANCE_COMMITTEE: "GRIEVANCE_COMMITTEE",
  STEWARD: "STEWARD",
  SAFETY_COMMITTEE: "SAFETY_COMMITTEE",
  POLITICAL_ACTION_COMMITTEE: "POLITICAL_ACTION_COMMITTEE",
  MEMBERSHIP_COMMITTEE: "MEMBERSHIP_COMMITTEE",
  BARGAINING_TEAM: "BARGAINING_TEAM",
  ELECTION_OBSERVER: "ELECTION_OBSERVER",
  LOCAL_ADMIN: "LOCAL_ADMIN",
  SUPER_ADMIN: "SUPER_ADMIN",
  GENERAL_MEMBER: "GENERAL_MEMBER",
};

// ============================================================================
// ROLE TEMPLATES
// ============================================================================

/**
 * Role templates define the default permission sets for each role type
 * When a new tenant is created, these templates are used to create
 * tenant-specific roles that CAN be customized
 */
export const ROLE_TEMPLATES = {
  // ==========================================================================
  // LEADERSHIP ROLES
  // ==========================================================================

  [ROLE_TEMPLATE_KEYS.PRESIDENT]: {
    name: "President",
    description: "Union President - Full administrative access to all features",
    permissions: {
      // Grievances
      [PERMISSIONS.VIEW_ALL_GRIEVANCES]: true,
      [PERMISSIONS.MANAGE_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_CONFIDENTIAL_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_GRIEVANCE_WORKFLOWS]: true,
      [PERMISSIONS.MANAGE_GRIEVANCE_WORKFLOWS]: true,
      [PERMISSIONS.FILE_GRIEVANCE_FOR_ANY_DEPARTMENT]: true,

      // Communications
      [PERMISSIONS.SEND_BULK_EMAIL]: true,
      [PERMISSIONS.SEND_BULK_SMS]: true,
      [PERMISSIONS.SEND_PUSH_NOTIFICATIONS]: true,
      [PERMISSIONS.CREATE_POSTS]: true,
      [PERMISSIONS.MANAGE_POSTS]: true,
      [PERMISSIONS.PIN_POSTS]: true,

      // Documents
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,
      [PERMISSIONS.MANAGE_DOCUMENTS]: true,
      [PERMISSIONS.DELETE_ANY_DOCUMENT]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,
      [PERMISSIONS.MANAGE_EVENTS]: true,
      [PERMISSIONS.DELETE_ANY_EVENT]: true,

      // Hall Reservations
      [PERMISSIONS.APPROVE_HALL_RESERVATIONS]: true,
      [PERMISSIONS.MANAGE_HALL_RESERVATIONS]: true,

      // Admin
      [PERMISSIONS.VIEW_ALL_USERS]: true,
      [PERMISSIONS.MANAGE_USERS]: true,
      [PERMISSIONS.MANAGE_USER_NOTES]: true,
      [PERMISSIONS.MANAGE_ROLES]: true,
      [PERMISSIONS.MANAGE_DEPARTMENTS]: true,
      [PERMISSIONS.VIEW_AUDIT_LOGS]: true,
      [PERMISSIONS.VIEW_ANALYTICS]: true,

      // Leadership
      [PERMISSIONS.MANAGE_LEADERSHIP]: true,

      // Financial
      [PERMISSIONS.VIEW_FINANCIALS]: true,
      [PERMISSIONS.MANAGE_FINANCIALS]: true,
      [PERMISSIONS.APPROVE_EXPENSES]: true,

      // Voting
      [PERMISSIONS.CREATE_VOTES]: true,
      [PERMISSIONS.MANAGE_VOTES]: true,

      // Store
      [PERMISSIONS.MANAGE_STORE]: true,
      [PERMISSIONS.VIEW_STORE_ORDERS]: true,

      // Bargaining
      [PERMISSIONS.VIEW_BARGAINING_NOTES]: true,
      [PERMISSIONS.MANAGE_BARGAINING_NOTES]: true,

      // Task Management
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
      [PERMISSIONS.VIEW_TASK_ANALYTICS]: true,

      // E-Board
      [PERMISSIONS.VIEW_EBOARD_UPDATES]: true,
      [PERMISSIONS.SUBMIT_EBOARD_UPDATES]: true,
      [PERMISSIONS.MANAGE_EBOARD_UPDATES]: true,

      // System
      [PERMISSIONS.MANAGE_SYSTEM_SETTINGS]: true,
    },
  },

  [ROLE_TEMPLATE_KEYS.VICE_PRESIDENT]: {
    name: "Vice President",
    description:
      "Union Vice President - Broad access to union operations and management",
    permissions: {
      // Grievances
      [PERMISSIONS.VIEW_ALL_GRIEVANCES]: true,
      [PERMISSIONS.MANAGE_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_CONFIDENTIAL_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_GRIEVANCE_WORKFLOWS]: true,
      [PERMISSIONS.MANAGE_GRIEVANCE_WORKFLOWS]: true,
      [PERMISSIONS.FILE_GRIEVANCE_FOR_ANY_DEPARTMENT]: true,

      // Communications
      [PERMISSIONS.SEND_BULK_EMAIL]: true,
      [PERMISSIONS.SEND_BULK_SMS]: true,
      [PERMISSIONS.SEND_PUSH_NOTIFICATIONS]: true,
      [PERMISSIONS.CREATE_POSTS]: true,
      [PERMISSIONS.MANAGE_POSTS]: true,
      [PERMISSIONS.PIN_POSTS]: true,

      // Documents
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,
      [PERMISSIONS.MANAGE_DOCUMENTS]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,
      [PERMISSIONS.MANAGE_EVENTS]: true,

      // Hall Reservations
      [PERMISSIONS.APPROVE_HALL_RESERVATIONS]: true,

      // Admin
      [PERMISSIONS.VIEW_ALL_USERS]: true,
      [PERMISSIONS.MANAGE_USER_NOTES]: true,
      [PERMISSIONS.VIEW_ANALYTICS]: true,

      // Voting
      [PERMISSIONS.CREATE_VOTES]: true,
      [PERMISSIONS.MANAGE_VOTES]: true,

      // Task Management
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
      [PERMISSIONS.VIEW_TASK_ANALYTICS]: true,

      // E-Board
      [PERMISSIONS.VIEW_EBOARD_UPDATES]: true,
      [PERMISSIONS.SUBMIT_EBOARD_UPDATES]: true,
    },
  },

  [ROLE_TEMPLATE_KEYS.SECRETARY]: {
    name: "Secretary",
    description:
      "Union Secretary - Manages communications, documents, and events",
    permissions: {
      // Grievances
      [PERMISSIONS.FILE_GRIEVANCE_FOR_ANY_DEPARTMENT]: true,

      // Communications
      [PERMISSIONS.SEND_BULK_EMAIL]: true,
      [PERMISSIONS.CREATE_POSTS]: true,
      [PERMISSIONS.MANAGE_POSTS]: true,
      [PERMISSIONS.PIN_POSTS]: true,

      // Documents
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,
      [PERMISSIONS.MANAGE_DOCUMENTS]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,
      [PERMISSIONS.MANAGE_EVENTS]: true,

      // Hall Reservations
      [PERMISSIONS.APPROVE_HALL_RESERVATIONS]: true,

      // Admin
      [PERMISSIONS.VIEW_ALL_USERS]: true,

      // Task Management
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,

      // E-Board
      [PERMISSIONS.VIEW_EBOARD_UPDATES]: true,
      [PERMISSIONS.SUBMIT_EBOARD_UPDATES]: true,
    },
  },

  [ROLE_TEMPLATE_KEYS.TREASURER]: {
    name: "Treasurer",
    description: "Union Treasurer - Manages financial data and reporting",
    permissions: {
      // Admin
      [PERMISSIONS.VIEW_ANALYTICS]: true,

      // Financial
      [PERMISSIONS.VIEW_FINANCIALS]: true,
      [PERMISSIONS.MANAGE_FINANCIALS]: true,
      [PERMISSIONS.APPROVE_EXPENSES]: true,

      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,

      // E-Board
      [PERMISSIONS.VIEW_EBOARD_UPDATES]: true,
      [PERMISSIONS.SUBMIT_EBOARD_UPDATES]: true,
    },
  },

  [ROLE_TEMPLATE_KEYS.TRUSTEE]: {
    name: "Trustee",
    description: "Union Trustee - Oversees operations and reviews audit logs",
    permissions: {
      // Admin
      [PERMISSIONS.VIEW_AUDIT_LOGS]: true,
      [PERMISSIONS.VIEW_ANALYTICS]: true,

      // Financial
      [PERMISSIONS.VIEW_FINANCIALS]: true,

      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,

      // Task Management
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
      [PERMISSIONS.VIEW_TASK_ANALYTICS]: true,

      // E-Board
      [PERMISSIONS.VIEW_EBOARD_UPDATES]: true,
      [PERMISSIONS.SUBMIT_EBOARD_UPDATES]: true,
    },
  },

  [ROLE_TEMPLATE_KEYS.EBOARD_REP]: {
    name: "EBoard Rep",
    description:
      "Executive Board Representative - Access to grievances and event management",
    permissions: {
      // Grievances
      [PERMISSIONS.VIEW_ALL_GRIEVANCES]: true,

      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,

      // Admin
      [PERMISSIONS.VIEW_ALL_USERS]: true,

      // Task Management
      [PERMISSIONS.VIEW_ALL_TASKS]: true,

      // E-Board
      [PERMISSIONS.VIEW_EBOARD_UPDATES]: true,
      [PERMISSIONS.SUBMIT_EBOARD_UPDATES]: true,
    },
  },

  // ==========================================================================
  // FUNCTIONAL ROLES
  // ==========================================================================

  [ROLE_TEMPLATE_KEYS.GRIEVANCE_COMMITTEE]: {
    name: "Grievance Committee Member",
    description:
      "Grievance Committee - Full access to view and manage all member grievances",
    permissions: {
      // Grievances
      [PERMISSIONS.VIEW_ALL_GRIEVANCES]: true,
      [PERMISSIONS.MANAGE_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_CONFIDENTIAL_GRIEVANCES]: true,

      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,
    },
  },

  [ROLE_TEMPLATE_KEYS.STEWARD]: {
    name: "Steward",
    description:
      "Union Steward - Handles member grievances and workplace issues",
    permissions: {
      // Grievances
      [PERMISSIONS.VIEW_ALL_GRIEVANCES]: true,
      [PERMISSIONS.MANAGE_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_CONFIDENTIAL_GRIEVANCES]: true,

      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,

      // Admin
      [PERMISSIONS.VIEW_ALL_USERS]: true,

      // Task Management
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
    },
  },

  [ROLE_TEMPLATE_KEYS.SAFETY_COMMITTEE]: {
    name: "Safety Committee Member",
    description:
      "Safety Committee - Manages safety-related communications and documents",
    permissions: {
      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,

      // Documents
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,

      // Task Management
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
    },
  },

  [ROLE_TEMPLATE_KEYS.POLITICAL_ACTION_COMMITTEE]: {
    name: "Political Action Committee Member",
    description:
      "Political Action Committee - Manages political events and communications",
    permissions: {
      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,
      [PERMISSIONS.SEND_BULK_EMAIL]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,
      [PERMISSIONS.MANAGE_EVENTS]: true,

      // Documents
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,

      // Task Management
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
    },
  },

  [ROLE_TEMPLATE_KEYS.MEMBERSHIP_COMMITTEE]: {
    name: "Membership Committee Member",
    description:
      "Membership Committee - Manages member communications and basic user management",
    permissions: {
      // Communications
      [PERMISSIONS.SEND_BULK_EMAIL]: true,
      [PERMISSIONS.CREATE_POSTS]: true,

      // Admin
      [PERMISSIONS.VIEW_ALL_USERS]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,

      // Task Management
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
    },
  },

  [ROLE_TEMPLATE_KEYS.BARGAINING_TEAM]: {
    name: "Bargaining Team Member",
    description:
      "Bargaining Team - Access to contract documents and bargaining notes",
    permissions: {
      // Documents
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,
      [PERMISSIONS.MANAGE_DOCUMENTS]: true,

      // Bargaining
      [PERMISSIONS.VIEW_BARGAINING_NOTES]: true,
      [PERMISSIONS.MANAGE_BARGAINING_NOTES]: true,

      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,

      // Task Management
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
    },
  },

  [ROLE_TEMPLATE_KEYS.ELECTION_OBSERVER]: {
    name: "Election Observer",
    description:
      "Designated observer for officer elections — can view participation stats, voter roll, audit ballots, and file eligibility challenges. Must be explicitly added to each election by an admin.",
    permissions: {
      // Voting
      [PERMISSIONS.OBSERVE_VOTE]: true,
      [PERMISSIONS.REVIEW_VOTER_ROLL]: true,
    },
  },

  // ==========================================================================
  // ADMINISTRATIVE ROLES
  // ==========================================================================

  [ROLE_TEMPLATE_KEYS.LOCAL_ADMIN]: {
    name: "Local Admin",
    description:
      "Local Administrator - Full tenant-level administrative access (excludes platform management)",
    permissions: {
      // Grievances
      [PERMISSIONS.VIEW_ALL_GRIEVANCES]: true,
      [PERMISSIONS.MANAGE_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_CONFIDENTIAL_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_GRIEVANCE_WORKFLOWS]: true,
      [PERMISSIONS.MANAGE_GRIEVANCE_WORKFLOWS]: true,

      // Communications
      [PERMISSIONS.SEND_BULK_EMAIL]: true,
      [PERMISSIONS.SEND_BULK_SMS]: true,
      [PERMISSIONS.SEND_PUSH_NOTIFICATIONS]: true,
      [PERMISSIONS.CREATE_POSTS]: true,
      [PERMISSIONS.MANAGE_POSTS]: true,
      [PERMISSIONS.PIN_POSTS]: true,

      // Documents
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,
      [PERMISSIONS.MANAGE_DOCUMENTS]: true,
      [PERMISSIONS.DELETE_ANY_DOCUMENT]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,
      [PERMISSIONS.MANAGE_EVENTS]: true,
      [PERMISSIONS.DELETE_ANY_EVENT]: true,

      // Hall Reservations
      [PERMISSIONS.APPROVE_HALL_RESERVATIONS]: true,
      [PERMISSIONS.MANAGE_HALL_RESERVATIONS]: true,

      // Admin
      [PERMISSIONS.VIEW_ALL_USERS]: true,
      [PERMISSIONS.MANAGE_USERS]: true,
      [PERMISSIONS.MANAGE_USER_NOTES]: true,
      [PERMISSIONS.MANAGE_ROLES]: true,
      [PERMISSIONS.MANAGE_DEPARTMENTS]: true,
      [PERMISSIONS.VIEW_AUDIT_LOGS]: true,
      [PERMISSIONS.VIEW_ANALYTICS]: true,

      // Leadership
      [PERMISSIONS.MANAGE_LEADERSHIP]: true,

      // Financial
      [PERMISSIONS.VIEW_FINANCIALS]: true,
      [PERMISSIONS.MANAGE_FINANCIALS]: true,
      [PERMISSIONS.APPROVE_EXPENSES]: true,

      // Voting
      [PERMISSIONS.CREATE_VOTES]: true,
      [PERMISSIONS.MANAGE_VOTES]: true,

      // Store
      [PERMISSIONS.MANAGE_STORE]: true,
      [PERMISSIONS.VIEW_STORE_ORDERS]: true,

      // Bargaining
      [PERMISSIONS.VIEW_BARGAINING_NOTES]: true,
      [PERMISSIONS.MANAGE_BARGAINING_NOTES]: true,

      // Task Management
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
      [PERMISSIONS.VIEW_TASK_ANALYTICS]: true,

      // E-Board Updates
      [PERMISSIONS.VIEW_EBOARD_UPDATES]: true,
      [PERMISSIONS.SUBMIT_EBOARD_UPDATES]: true,
      [PERMISSIONS.MANAGE_EBOARD_UPDATES]: true,

      // System (tenant-level only, not platform-level)
      [PERMISSIONS.MANAGE_SYSTEM_SETTINGS]: true,
    },
  },

  [ROLE_TEMPLATE_KEYS.SUPER_ADMIN]: {
    name: "Super Admin",
    description:
      "Platform Super Administrator - Full access to all features across all tenants",
    permissions: {
      // ALL PERMISSIONS
      [PERMISSIONS.VIEW_ALL_GRIEVANCES]: true,
      [PERMISSIONS.MANAGE_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_CONFIDENTIAL_GRIEVANCES]: true,
      [PERMISSIONS.SEND_BULK_EMAIL]: true,
      [PERMISSIONS.SEND_BULK_SMS]: true,
      [PERMISSIONS.SEND_PUSH_NOTIFICATIONS]: true,
      [PERMISSIONS.CREATE_POSTS]: true,
      [PERMISSIONS.MANAGE_POSTS]: true,
      [PERMISSIONS.PIN_POSTS]: true,
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,
      [PERMISSIONS.MANAGE_DOCUMENTS]: true,
      [PERMISSIONS.DELETE_ANY_DOCUMENT]: true,
      [PERMISSIONS.CREATE_EVENTS]: true,
      [PERMISSIONS.MANAGE_EVENTS]: true,
      [PERMISSIONS.DELETE_ANY_EVENT]: true,
      [PERMISSIONS.APPROVE_HALL_RESERVATIONS]: true,
      [PERMISSIONS.MANAGE_HALL_RESERVATIONS]: true,
      [PERMISSIONS.VIEW_ALL_USERS]: true,
      [PERMISSIONS.MANAGE_USERS]: true,
      [PERMISSIONS.MANAGE_USER_NOTES]: true,
      [PERMISSIONS.MANAGE_ROLES]: true,
      [PERMISSIONS.MANAGE_DEPARTMENTS]: true,
      [PERMISSIONS.VIEW_AUDIT_LOGS]: true,
      [PERMISSIONS.VIEW_ANALYTICS]: true,
      [PERMISSIONS.MANAGE_LEADERSHIP]: true,
      [PERMISSIONS.VIEW_FINANCIALS]: true,
      [PERMISSIONS.MANAGE_FINANCIALS]: true,
      [PERMISSIONS.APPROVE_EXPENSES]: true,
      [PERMISSIONS.CREATE_VOTES]: true,
      [PERMISSIONS.MANAGE_VOTES]: true,
      [PERMISSIONS.MANAGE_STORE]: true,
      [PERMISSIONS.VIEW_STORE_ORDERS]: true,
      [PERMISSIONS.VIEW_BARGAINING_NOTES]: true,
      [PERMISSIONS.MANAGE_BARGAINING_NOTES]: true,
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
      [PERMISSIONS.VIEW_TASK_ANALYTICS]: true,
      [PERMISSIONS.MANAGE_SYSTEM_SETTINGS]: true,

      // Platform-level permissions
      [PERMISSIONS.MANAGE_TENANTS]: true,
    },
  },

  [ROLE_TEMPLATE_KEYS.GENERAL_MEMBER]: {
    name: "General Member",
    description:
      "Default role for all union members - Basic access to member features",
    permissions: {
      // General members have no special permissions
      // They can:
      // - View their own grievances (handled by ownership check, not permission)
      // - View documents (default access)
      // - View events and RSVP (default access)
      // - Request hall reservations (default access)
      // - View their own profile and limited directory
      // - Vote on polls (when implemented)
      // No explicit permissions needed - basic functionality is default
    },
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get a role template by key
 * @param {string} templateKey - Template key (e.g., ROLE_TEMPLATE_KEYS.PRESIDENT)
 * @returns {Object|null} Template object or null if not found
 */
export function getRoleTemplate(templateKey) {
  return ROLE_TEMPLATES[templateKey] || null;
}

/**
 * Get all role templates as an array
 * @returns {Array} Array of template objects with keys
 */
export function getAllRoleTemplates() {
  return Object.entries(ROLE_TEMPLATES).map(([key, template]) => ({
    templateKey: key,
    ...template,
  }));
}

/**
 * Check if a template key is valid
 * @param {string} templateKey - Template key to validate
 * @returns {boolean} True if valid
 */
export function isValidTemplateKey(templateKey) {
  return Object.values(ROLE_TEMPLATE_KEYS).includes(templateKey);
}

/**
 * Get template keys for a specific category
 * @param {string} category - Category name (e.g., 'leadership', 'functional', 'administrative')
 * @returns {Array<string>} Array of template keys
 */
export function getTemplateKeysByCategory(category) {
  const categories = {
    leadership: [
      ROLE_TEMPLATE_KEYS.PRESIDENT,
      ROLE_TEMPLATE_KEYS.VICE_PRESIDENT,
      ROLE_TEMPLATE_KEYS.SECRETARY,
      ROLE_TEMPLATE_KEYS.TREASURER,
      ROLE_TEMPLATE_KEYS.TRUSTEE,
      ROLE_TEMPLATE_KEYS.EBOARD_REP,
    ],
    functional: [
      ROLE_TEMPLATE_KEYS.GRIEVANCE_COMMITTEE,
      ROLE_TEMPLATE_KEYS.STEWARD,
      ROLE_TEMPLATE_KEYS.SAFETY_COMMITTEE,
      ROLE_TEMPLATE_KEYS.POLITICAL_ACTION_COMMITTEE,
      ROLE_TEMPLATE_KEYS.MEMBERSHIP_COMMITTEE,
      ROLE_TEMPLATE_KEYS.BARGAINING_TEAM,
      ROLE_TEMPLATE_KEYS.ELECTION_OBSERVER,
    ],
    administrative: [
      ROLE_TEMPLATE_KEYS.LOCAL_ADMIN,
      ROLE_TEMPLATE_KEYS.SUPER_ADMIN,
      ROLE_TEMPLATE_KEYS.GENERAL_MEMBER,
    ],
  };

  return categories[category] || [];
}
